import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { SYLLABUS, ADD_SYLLABUS_ITEM } from '../graphql';
import { BookOpen, ChevronDown, ChevronRight, ExternalLink, Plus } from 'lucide-react';

export default function SyllabusPage() {
  const [filters, setFilters] = useState({ subject: '', semester: '', branch: '' });
  const { data, loading } = useQuery(SYLLABUS, { variables: {
    subject: filters.subject || null,
    semester: filters.semester ? parseInt(filters.semester) : null,
    branch: filters.branch || null,
  }});
  const [addItem] = useMutation(ADD_SYLLABUS_ITEM, { refetchQueries: [SYLLABUS] });
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ subject: '', semester: '', branch: '', unit: '', title: '', topics: '', resources: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    const resources = form.resources.split('\n').map(r => {
      const [label, url] = r.split('|').map(s=>s.trim());
      return label && url ? { label, url } : null;
    }).filter(Boolean);
    await addItem({ variables: {
      ...form,
      semester: parseInt(form.semester),
      unit: parseInt(form.unit),
      topics: form.topics.split('\n').map(t=>t.trim()).filter(Boolean),
      resources,
    }});
    setForm({ subject: '', semester: '', branch: '', unit: '', title: '', topics: '', resources: '' });
    setShowForm(false);
  };

  const items = data?.syllabus || [];
  // Group by subject
  const bySubject = {};
  items.forEach(item => { (bySubject[item.subject] = bySubject[item.subject] || []).push(item); });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Syllabus Repository</h1>
          <p className="text-gray-500 text-sm mt-1">Organized syllabus with unit topics and study resources — no more hunting PDFs</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Add Syllabus
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <input className="input flex-1 min-w-32" placeholder="Subject..." value={filters.subject} onChange={e=>setFilters({...filters,subject:e.target.value})} />
        <select className="input w-32" value={filters.semester} onChange={e=>setFilters({...filters,semester:e.target.value})}>
          <option value="">All Sems</option>
          {[1,2,3,4,5,6,7,8].map(s=><option key={s} value={s}>Sem {s}</option>)}
        </select>
        <input className="input w-32" placeholder="Branch..." value={filters.branch} onChange={e=>setFilters({...filters,branch:e.target.value})} />
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Add Syllabus Unit</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input className="input" required value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Semester</label>
              <input className="input" type="number" min="1" max="8" required value={form.semester} onChange={e=>setForm({...form,semester:e.target.value})} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Branch (optional)</label>
              <input className="input" value={form.branch} onChange={e=>setForm({...form,branch:e.target.value})} placeholder="CSE" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Unit No.</label>
              <input className="input" type="number" min="1" max="10" required value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} /></div>
            <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Unit Title</label>
              <input className="input" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Sorting Algorithms" /></div>
            <div className="md:col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Topics (one per line)</label>
              <textarea className="input !h-24 resize-none" value={form.topics} onChange={e=>setForm({...form,topics:e.target.value})} placeholder={"Quick Sort\nMerge Sort\nHeap Sort\nTime Complexity Analysis"} /></div>
            <div className="md:col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Resources (one per line: Label | URL)</label>
              <textarea className="input !h-20 resize-none" value={form.resources} onChange={e=>setForm({...form,resources:e.target.value})} placeholder={"NPTEL Video | https://nptel.ac.in/...\nGeeksForGeeks | https://geeksforgeeks.org/..."} /></div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Save</button>
            <button className="btn-secondary" type="button" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>
      ) : Object.keys(bySubject).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <BookOpen size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No syllabus added yet.</p>
          <p className="text-sm mt-1">Add your subject syllabi so everyone can access them in one place.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(bySubject).map(([subject, units]) => {
            const isOpen = expanded[subject];
            return (
              <div key={subject} className="card !p-0 overflow-hidden">
                <button className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                  onClick={() => setExpanded({...expanded, [subject]: !isOpen})}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <BookOpen size={18} className="text-indigo-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{subject}</p>
                      <p className="text-xs text-gray-500">Sem {units[0].semester}{units[0].branch ? ` · ${units[0].branch}` : ''} · {units.length} unit{units.length!==1?'s':''}</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100">
                    {units.map(unit => (
                      <div key={unit.id} className="px-6 py-4 border-b border-gray-50 last:border-0">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {unit.unit}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-2">{unit.title}</p>
                            {unit.topics.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {unit.topics.map(t => (
                                  <span key={t} className="badge bg-gray-100 text-gray-600 text-xs">{t}</span>
                                ))}
                              </div>
                            )}
                            {unit.resources.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {unit.resources.map(r => (
                                  <a key={r.label} href={r.url} target="_blank" rel="noreferrer"
                                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2 py-1 rounded-lg">
                                    <ExternalLink size={11} /> {r.label}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
